/**
 * Argument coercion + formatting helpers shared by the tool executors.
 *
 * Gemini function-call arguments arrive as a loosely-typed object. These
 * helpers narrow them safely without `any` and reject malformed input early.
 */

import { toMillis } from "../lib/timestamps";

export function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

export function asBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

export function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function asEnum<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

export function requireString(args: Record<string, unknown>, key: string): string {
  const v = asString(args[key]);
  if (v === undefined) throw new Error(`Missing required parameter: "${key}".`);
  return v;
}

export function requireNumber(args: Record<string, unknown>, key: string): number {
  const v = asNumber(args[key]);
  if (v === undefined) throw new Error(`Missing or invalid numeric parameter: "${key}".`);
  return v;
}

/**
 * Normalise a date the model supplied into a full ISO-8601 timestamp.
 * Accepts date-only strings ("2026-06-14") by anchoring them to midday UTC,
 * which keeps the displayed day stable across timezones.
 */
export function normalizeDate(value: unknown): string | undefined {
  const s = asString(value);
  if (s === undefined) return undefined;
  const candidate = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00.000Z` : s;
  const ms = Date.parse(candidate);
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid date: "${s}". Use ISO-8601, e.g. 2026-06-14.`);
  }
  return new Date(ms).toISOString();
}

/** Coerce a stored Firestore timestamp/string into an ISO string, or null. */
export function isoOrNull(value: unknown): string | null {
  if (value == null) return null;
  const ms = toMillis(value);
  return ms === 0 ? null : new Date(ms).toISOString();
}

/** Whole days from now until `value` (negative if in the past), or null. */
export function daysUntil(value: unknown): number | null {
  if (value == null) return null;
  const ms = toMillis(value);
  if (ms === 0) return null;
  return Math.ceil((ms - Date.now()) / 86_400_000);
}
